import React, { useState, useEffect, useMemo } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { labelStyles, requiredStyles, getSelectStyles } from '../styles';
import { FormInput, FormCheckbox } from '~/common/form/FormInput';
import Select from 'react-select';
import { GENDER_OPTIONS } from '~/constants/metadata';
import { format } from 'date-fns';

interface Props {
    person: any;
    patient: any;
    preferredLanguages: string[];
    maritalStatuses: string[];
    ethnicities: string[];
}

// Helper to check if a react-select value has content
const hasSelectValue = (value: any): boolean => {
    if (!value) return false;
    if (typeof value === 'object') {
        return !!(value.value && value.value.trim() !== '');
    }
    return false;
};

const AdditionalPersonInfo: React.FC<Props> = ({
    person,
    patient,
    preferredLanguages,
    maritalStatuses,
    ethnicities,
}) => {
    const { control, setValue } = useFormContext();
    const [isDeceased, setIsDeceased] = useState(!!person.patient_is_deceased);
    const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);

    useEffect(() => {
        // Convert null to empty string for date fields to avoid React warnings
        setValue('person.birth_date', person.birth_date || '');
        setValue('person.age', person.age);
        setValue(
            'person.patient_is_deceased',
            person.patient_is_deceased || false,
        );
        setValue('person.death_date', person.death_date || '');
        setValue('person.pronouns', person.pronouns || '');
        setValue('person.gender', {
            label: person.gender,
            value: person.gender,
        });
        setValue('person.marital_status', {
            label: person.marital_status,
            value: person.marital_status,
        });
        setValue('person.preferred_language', {
            label: person.preferred_language,
            value: person.preferred_language,
        });

        // Handle ethnicity - it might already be an object or just a string
        const ethnicityValue =
            typeof patient?.ethnicity === 'object' &&
            patient?.ethnicity !== null
                ? patient.ethnicity.value || patient.ethnicity.label
                : patient?.ethnicity;
        setValue('patient.ethnicity', {
            label: ethnicityValue,
            value: ethnicityValue,
        });
    }, [person, patient, setValue]);

    const genderOptions = GENDER_OPTIONS.map((gender) => ({
        value: gender,
        label: gender,
    }));
    const maritalStatusOptions = (maritalStatuses || ['']).map(
        (maritalStatus) => {
            return { value: maritalStatus, label: maritalStatus };
        },
    );
    const preferredLanguageOptions = (preferredLanguages || ['']).map(
        (language) => {
            return { value: language, label: language };
        },
    );
    const ethnicityOptions = (ethnicities || []).map((ethnicity) => {
        return { value: ethnicity, label: ethnicity };
    });

    return (
        <>
            <div className='flex flex-row gap-[8px]'>
                <div className='w-1/2'>
                    <FormInput
                        name='person.birth_date'
                        label='Date of Birth'
                        type='date'
                        required
                        max={today}
                        rules={{
                            required: 'Date of Birth is required',
                            validate: (value: string) => {
                                if (value && value > today) {
                                    return 'Date of Birth cannot be in the future';
                                }
                                return true;
                            },
                        }}
                    />
                </div>
            </div>

            <FormCheckbox
                name='person.patient_is_deceased'
                label='Client is deceased'
                className='mt-4'
                onChange={(checked) => {
                    setIsDeceased(checked);
                    if (!checked) {
                        setValue('person.death_date', '');
                    }
                }}
            />

            {isDeceased && (
                <div className='mt-4'>
                    <FormInput
                        name='person.death_date'
                        label='Date of Death'
                        type='date'
                    />
                </div>
            )}

            <div className='flex flex-row gap-[8px]'>
                <div className='w-1/2'>
                    <label
                        htmlFor='cid-gender'
                        className={`${labelStyles} w-full grow`}
                    >
                        Gender<span className={requiredStyles}>*</span>
                    </label>
                    <Controller
                        name='person.gender'
                        control={control}
                        render={({ field }) => (
                            <Select
                                menuPlacement='auto'
                                id='cid-gender'
                                {...field}
                                options={genderOptions}
                                className='w-full'
                                classNamePrefix={'cid-gender'}
                                styles={getSelectStyles(
                                    hasSelectValue(field.value),
                                )}
                            />
                        )}
                    />
                </div>

                <div className='w-1/2'>
                    <FormInput name='person.pronouns' label='Pronouns' />
                </div>
            </div>

            <label htmlFor='cid-marital-status' className={labelStyles}>
                Marital Status<span className={requiredStyles}>*</span>
            </label>
            <Controller
                name='person.marital_status'
                control={control}
                render={({ field }) => (
                    <Select
                        menuPlacement='auto'
                        id='cid-marital-status'
                        {...field}
                        options={maritalStatusOptions}
                        className='w-full'
                        classNamePrefix={'cid-marital-status'}
                        styles={getSelectStyles(hasSelectValue(field.value))}
                    />
                )}
            />

            <label htmlFor='cid-preferred-language' className={labelStyles}>
                Preferred Language<span className={requiredStyles}>*</span>
            </label>
            <Controller
                name='person.preferred_language'
                control={control}
                render={({ field }) => (
                    <Select
                        menuPlacement='auto'
                        id='cid-preferred-language'
                        {...field}
                        options={preferredLanguageOptions}
                        className='w-full'
                        classNamePrefix={'cid-preferred-language'}
                        styles={getSelectStyles(hasSelectValue(field.value))}
                    />
                )}
            />

            <label htmlFor='cid-ethnicity' className={labelStyles}>
                Ethnicity<span className={requiredStyles}>*</span>
            </label>
            <Controller
                name='patient.ethnicity'
                control={control}
                render={({ field }) => (
                    <Select
                        menuPlacement='auto'
                        id='cid-ethnicity'
                        {...field}
                        options={ethnicityOptions}
                        className='w-full'
                        classNamePrefix={'cid-ethnicity'}
                        styles={getSelectStyles(hasSelectValue(field.value))}
                    />
                )}
            />
        </>
    );
};

export default AdditionalPersonInfo;
